import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users, ClipboardList, TrendingUp, AlertCircle, Sparkles, RefreshCw, MessagesSquare, Link as LinkIcon, ShieldCheck, XCircle, Zap } from "lucide-react";
import { api, clearSession, getUser } from "@/lib/api";
import BrandHeader from "@/components/BrandHeader";
import { TruncatedBlocksPanel } from "@/components/admin/TruncatedBlocksPanel";
import { TableroFlagsPanel } from "@/components/admin/TableroFlagsPanel";
import { CadenaFlagPanel } from "@/components/admin/CadenaFlagPanel";
import { TESTIDS } from "@/constants/testIds";
import { formatDate, levelBadgeClass } from "@/lib/format";

const AREA_LABEL = {
  A: "Marca empleadora",
  B: "Selección",
  C: "Onboarding",
  D: "Evaluación",
  E: "Feedback",
  F: "Desarrollo",
};

export default function Admin() {
  const navigate = useNavigate();
  const user = getUser();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [pendingActivations, setPendingActivations] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [resendingId, setResendingId] = useState(null);
  const [manualActivatingId, setManualActivatingId] = useState(null);
  const [manualResult, setManualResult] = useState(null); // {email, password}
  const [refundSearch, setRefundSearch] = useState("");
  const [refundFrom, setRefundFrom] = useState("");
  const [refundTo, setRefundTo] = useState("");
  const [expandedUser, setExpandedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [failedPersos, setFailedPersos] = useState([]);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [perceptionOverview, setPerceptionOverview] = useState(null);
  const [perceptionTests, setPerceptionTests] = useState([]);
  const [perceptionLoading, setPerceptionLoading] = useState(false);
  const [sectorsUsage, setSectorsUsage] = useState(null);
  const [failedConsultings, setFailedConsultings] = useState([]);
  const [regeneratingConsultingId, setRegeneratingConsultingId] = useState(null);
  const [resendingEmailId, setResendingEmailId] = useState(null);

  async function refreshFailedConsultings() {
    try {
      const r = await api.get("/admin/consulting/failed");
      setFailedConsultings(r.data.items || []);
    } catch { /* silencioso */ }
  }

  async function regenerateFailedConsulting(item) {
    if (regeneratingConsultingId) return;
    if (!window.confirm(
      `¿Regenerar el informe de "${item.company_name}" (${item.user_email})?\n\n` +
      `${item.is_stuck ? "Estaba ATASCADO en generating >30 min." : `Falló con: ${(item.error || "").slice(0, 200)}`}\n\n` +
      `Se lanzará una nueva generación en background (~2-4 min). El cliente recibirá el email cuando esté lista.`
    )) return;
    setRegeneratingConsultingId(item.id);
    try {
      const r = await api.post(`/admin/consulting/${item.id}/regenerate`);
      if (r.data?.already_ready) {
        toast.success("El test ya estaba en READY. No se regenera.");
      } else {
        toast.success(`Regeneración lanzada · ${item.company_name}. Comprobación en 60 s.`);
      }
      setTimeout(() => { refreshFailedConsultings(); }, 60_000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos lanzar la regeneración");
    } finally { setRegeneratingConsultingId(null); }
  }

  async function resendEmailForConsulting(item) {
    if (resendingEmailId) return;
    if (!window.confirm(
      `¿Reenviar el email "informe listo" a ${item.user_email}?\n\n` +
      `Motivo del bloqueo previo: ${(item.notification_last_error || "-").slice(0, 200)}\n\n` +
      `Si el envío inmediato falla, el watchdog seguirá reintentando durante los próximos minutos.`
    )) return;
    setResendingEmailId(item.id);
    try {
      const r = await api.post(`/admin/consulting/${item.id}/resend-email`);
      if (r.data?.ok) {
        toast.success(`Email reenviado a ${item.user_email}.`);
      } else {
        toast.warning(r.data?.message || "El envío inmediato falló, el watchdog lo reintentará.");
      }
      setTimeout(() => { refreshFailedConsultings(); }, 10_000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos reenviar el email");
    } finally { setResendingEmailId(null); }
  }

  async function refreshPerception() {
    setPerceptionLoading(true);
    try {
      const [ov, ts] = await Promise.all([
        api.get("/admin/perception/overview"),
        api.get("/admin/perception/tests"),
      ]);
      setPerceptionOverview(ov.data);
      setPerceptionTests(ts.data.items || []);
    } catch (_e) {
      toast.error("No pudimos cargar los datos de percepciones");
    } finally { setPerceptionLoading(false); }
  }

  async function refreshFailedPersos() {
    try {
      const { data } = await api.get("/admin/personalizations/failed");
      setFailedPersos(data.items || []);
    } catch (_e) {
      // silent — no queremos toast si es un fallo puntual del refresh
    }
  }

  useEffect(() => {
    if (!user?.is_admin) {
      navigate("/dashboard");
      return;
    }
    (async () => {
      try {
        const [s, u, p, pa, rf, fp, pov, pts, sec, fc] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/users"),
          api.get("/admin/pending"),
          api.get("/admin/pending-activations"),
          api.get("/admin/refunds"),
          api.get("/admin/personalizations/failed"),
          api.get("/admin/perception/overview"),
          api.get("/admin/perception/tests"),
          api.get("/admin/consulting/sectors-usage"),
          api.get("/admin/consulting/failed"),
        ]);
        setStats(s.data);
        setUsers(u.data.users);
        setPending(p.data.pending);
        setPendingActivations(pa.data.items || []);
        setRefunds(rf.data.items || []);
        setFailedPersos(fp.data.items || []);
        setPerceptionOverview(pov.data);
        setPerceptionTests(pts.data.items || []);
        setSectorsUsage(sec.data);
        setFailedConsultings(fc.data.items || []);
      } catch (_e) {
        toast.error("No pudimos cargar los datos del admin");
      }
    })();
  }, [navigate, user?.is_admin]);

  async function regeneratePersonalization(item) {
    if (regeneratingId) return;
    const emailLabel = item.email || "el cliente";
    if (!window.confirm(
      `¿Regenerar la personalización de ${emailLabel} y REENVIARLE el email con el PDF actualizado?\n\n` +
      `Recibirá un nuevo correo con la interpretación LLM y los PDFs adjuntos regenerados (~30-60 s).`
    )) return;
    setRegeneratingId(item.id);
    try {
      await api.post(`/admin/diagnostic/${item.id}/regenerate-personalization`);
      toast.success(
        `Regenerando personalización y reenviando email a ${emailLabel}. El correo llegará en ~30-60 s.`,
        { duration: 6000 }
      );
      // Refresh optimista tras 45s (LLM ~20s + PDFs ~15s + email ~5s + margen)
      setTimeout(refreshFailedPersos, 45000);
      // Refresh inmediato para reflejar el status "pending"
      await refreshFailedPersos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos disparar la regeneración");
    } finally {
      setRegeneratingId(null);
    }
  }

  async function resendActivation(item) {
    if (resendingId) return;
    if (!window.confirm(`¿Reenviar email de activación a ${item.email}? Se renovará el enlace para 24h más.`)) return;
    setResendingId(item.id);
    try {
      await api.post(`/admin/pending-activations/${item.id}/resend`);
      toast.success(`Email reenviado a ${item.email}`);
      const pa = await api.get("/admin/pending-activations");
      setPendingActivations(pa.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos reenviar el email");
    } finally {
      setResendingId(null);
    }
  }

  async function manualActivate(item) {
    if (manualActivatingId) return;
    if (!window.confirm(`¿Activar manualmente a ${item.email}? Se generará una contraseña temporal segura que deberás comunicar al cliente por otro canal.`)) return;
    setManualActivatingId(item.id);
    try {
      const { data } = await api.post(`/admin/pending-activations/${item.id}/manual-activate`);
      setManualResult({ email: data.email, password: data.temporary_password });
      const pa = await api.get("/admin/pending-activations");
      setPendingActivations(pa.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos activar la cuenta");
    } finally {
      setManualActivatingId(null);
    }
  }

  const filteredRefunds = refunds.filter((r) => {
    if (refundSearch && !((r.email || "").toLowerCase().includes(refundSearch.toLowerCase()))) return false;
    if (refundFrom && r.refunded_at && r.refunded_at < refundFrom) return false;
    if (refundTo && r.refunded_at && r.refunded_at > refundTo + "T23:59:59") return false;
    return true;
  });

  async function openUser(email) {
    if (expandedUser === email) {
      setExpandedUser(null);
      setUserDetail(null);
      return;
    }
    setExpandedUser(email);
    setUserDetail(null);
    try {
      const { data } = await api.get(`/admin/user/${encodeURIComponent(email)}`);
      setUserDetail(data);
    } catch (_e) {
      toast.error("No pudimos cargar el detalle");
    }
  }

  function logout() {
    clearSession();
    navigate("/");
  }

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid={TESTIDS.admin.root}>
      <BrandHeader
        right={
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="btn-ghost">
              <ArrowLeft className="w-4 h-4" /> Área personal
            </Link>
            <button className="btn-ghost" onClick={logout}>
              Salir
            </button>
          </div>
        }
      />
      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="fade-in-up mb-8">
          <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0f6e56] mb-3">
            Panel administrador
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-gray-900">
            Métricas de la plataforma
          </h1>
        </div>

        {/* KPI cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 fade-in-up">
            <Kpi icon={<Users className="w-4 h-4" />} label="Usuarios reales" value={stats.total_users} />
            <Kpi icon={<ClipboardList className="w-4 h-4" />} label="Diagnósticos reales" value={stats.total_diagnostics_real} sub={`${stats.test_diagnostics} de test admin`} />
            <Kpi icon={<TrendingUp className="w-4 h-4" />} label="Media global" value={`${stats.avg_overall}/120`} />
            <Kpi icon={<AlertCircle className="w-4 h-4" />} label="Pendientes de completar" value={pending.length} />
          </div>
        )}

        {/* Averages by area */}
        {stats && (
          <div className="card fade-in-up mb-10">
            <h3 className="font-display text-lg font-bold text-gray-900 mb-4">
              Puntuación media por área (excluye datos de test)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              {Object.entries(stats.avg_areas).map(([c, v]) => (
                <div key={c} className="rounded-xl border border-gray-100 p-4 text-center">
                  <div className="w-9 h-9 mx-auto rounded-lg bg-[#0f6e56]/8 text-[#0f6e56] font-display font-bold flex items-center justify-center mb-2">{c}</div>
                  <div className="text-[11px] text-gray-500 leading-snug">{AREA_LABEL[c]}</div>
                  <div className="mt-2 font-display text-2xl font-bold text-[#0f6e56]">
                    {v}<span className="text-sm text-gray-400">/20</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-6 flex-wrap">
          <TabBtn active={tab === "overview"} onClick={() => setTab("overview")}>Usuarios ({users.length})</TabBtn>
          <TabBtn active={tab === "pending"} onClick={() => setTab("pending")}>Pendientes ({pending.length})</TabBtn>
          <TabBtn active={tab === "activations"} onClick={() => setTab("activations")} data-testid="admin-tab-activations">Sin activar ({pendingActivations.length})</TabBtn>
          <TabBtn active={tab === "refunds"} onClick={() => setTab("refunds")} data-testid="admin-tab-refunds">Reembolsos ({refunds.length})</TabBtn>
          <TabBtn
            active={tab === "personalizations"}
            onClick={() => setTab("personalizations")}
            data-testid="admin-tab-personalizations"
            highlight={failedPersos.length > 0}
          >
            Personalizaciones fallidas ({failedPersos.length})
          </TabBtn>
          <TabBtn
            active={tab === "perceptions"}
            onClick={() => setTab("perceptions")}
            data-testid="admin-tab-perceptions"
          >
            Percepciones ({perceptionOverview?.tests?.with_token ?? 0})
          </TabBtn>
          <TabBtn
            active={tab === "consulting-failed"}
            onClick={() => setTab("consulting-failed")}
            data-testid="admin-tab-consulting-failed"
            highlight={failedConsultings.length > 0}
          >
            Consultorías fallidas ({failedConsultings.length})
          </TabBtn>
          <TabBtn active={tab === "truncated"} onClick={() => setTab("truncated")} data-testid="admin-tab-truncated">
            Bloques cortados
          </TabBtn>
          <TabBtn active={tab === "tablero"} onClick={() => setTab("tablero")} data-testid="admin-tab-tablero">
            Interruptores
          </TabBtn>
        </div>

        {tab === "truncated" && <TruncatedBlocksPanel />}
        {tab === "tablero" && <><TableroFlagsPanel /><CadenaFlagPanel /></>}

        {tab === "overview" && (
          <div className="space-y-3">
            {users.map((u) => (
              <div key={u.email} className="rounded-2xl bg-white border border-gray-100 overflow-hidden" data-testid={TESTIDS.admin.userRow}>
                <button
                  onClick={() => openUser(u.email)}
                  className="w-full p-5 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full brand-gradient text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {u.email.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className="font-medium text-gray-900 truncate min-w-0" title={u.email}>{u.email}</span>
                      {u.is_admin && <span className="badge bg-gray-100 text-gray-600 flex-shrink-0">Admin</span>}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Alta: {u.created_at ? formatDate(u.created_at) : "—"}
                    </div>
                  </div>
                  <div className="hidden md:block text-right">
                    <div className="text-xs text-gray-500">Diagnósticos</div>
                    <div className="font-display text-lg font-bold text-gray-900">{u.diagnostics_count}</div>
                  </div>
                  <div className="hidden md:block text-right min-w-[110px]">
                    <div className="text-xs text-gray-500">Última puntuación</div>
                    <div className="font-display font-bold text-[#0f6e56]">
                      {u.last_score !== null ? `${u.last_score}/120` : "—"}
                    </div>
                  </div>
                  {u.last_level && (
                    <span className="hidden sm:inline-flex flex-shrink-0">
                      <span className={levelBadgeClass(u.last_level)}>{u.last_level}</span>
                    </span>
                  )}
                </button>
                {expandedUser === u.email && (
                  <div className="border-t border-gray-100 bg-gray-50/50 p-5">
                    {!userDetail ? (
                      <div className="text-sm text-gray-500">Cargando…</div>
                    ) : userDetail.diagnostics.length === 0 ? (
                      <div className="text-sm text-gray-500">Sin diagnósticos.</div>
                    ) : (
                      <div className="space-y-3">
                        {userDetail.diagnostics.map((d) => (
                          <div key={d.id} className="bg-white rounded-xl border border-gray-100 p-4">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div>
                                <div className="text-sm text-gray-500">{formatDate(d.created_at)}</div>
                                <div className="font-display font-bold text-gray-900">
                                  {d.overall_score}/120 · <span className={levelBadgeClass(d.overall_level)}>{d.overall_level}</span>
                                  {d.is_test_data && <span className="ml-2 badge bg-gray-100 text-gray-600">Test admin</span>}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                {d.area_rows.map((a) => (
                                  <div key={a.code} className="text-center px-2">
                                    <div className="text-[10px] text-gray-400">{a.code}</div>
                                    <div className="text-sm font-semibold text-[#0f6e56]">{a.score}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "pending" && (
          <div className="space-y-3">
            {pending.length === 0 ? (
              <div className="card text-center text-gray-500 py-10">
                No hay usuarios pendientes de completar el diagnóstico. 🎯
              </div>
            ) : (
              pending.map((p) => (
                <div key={p.email} className="rounded-2xl bg-white border border-amber-100 p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{p.email}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Cuenta creada: {p.created_at ? formatDate(p.created_at) : "—"}
                    </div>
                  </div>
                  <span className="badge badge-desarrollo">Sin diagnóstico</span>
                </div>
              ))
            )}
          </div>
        )}
        {tab === "activations" && (
          <div className="space-y-3" data-testid="admin-activations-list">
            {pendingActivations.length === 0 ? (
              <div className="card text-center text-gray-500 py-10">
                No hay cuentas pendientes de activación. 🎯
              </div>
            ) : (
              pendingActivations.map((p) => {
                const stale = (p.hours_pending || 0) >= 48;
                return (
                  <div key={p.id} className="rounded-2xl bg-white border border-amber-100 p-5 flex items-center gap-4 flex-wrap" data-testid="admin-activation-row">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${stale ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                      <AlertCircle className="w-5 h-5" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{p.email}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Alta {p.created_at ? formatDate(p.created_at) : "—"} · {p.hours_pending ?? "?"} h pendiente
                        {p.reminder_sent_at && " · recordatorio enviado"}
                      </div>
                    </div>
                    <div className="flex gap-2 items-center">
                      {stale && <span className="badge bg-red-100 text-red-700">&gt;48h</span>}
                      {(p.products || []).map((pr) => (
                        <span key={pr} className="badge bg-gray-100 text-gray-600">{pr}</span>
                      ))}
                      <button
                        onClick={() => resendActivation(p)}
                        disabled={resendingId === p.id}
                        className="rounded-full px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60"
                        style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
                        data-testid={`admin-resend-activation-${p.id}`}
                      >
                        {resendingId === p.id ? "Enviando…" : "Reenviar email"}
                      </button>
                      <button
                        onClick={() => manualActivate(p)}
                        disabled={manualActivatingId === p.id}
                        className="rounded-full px-4 py-2 text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60"
                        style={{ background: "white", color: "#0F6E56", border: "1.5px solid #0F6E56" }}
                        data-testid={`admin-manual-activate-${p.id}`}
                        title="Genera una contraseña temporal para el cliente"
                      >
                        {manualActivatingId === p.id ? "Activando…" : "Activar manual"}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {tab === "refunds" && (
          <div className="space-y-3" data-testid="admin-refunds-list">
            {refunds.length > 0 && (
              <div className="rounded-2xl bg-white border border-gray-100 p-4 flex flex-wrap gap-3 items-center">
                <input
                  type="text"
                  placeholder="Buscar por email…"
                  value={refundSearch}
                  onChange={(e) => setRefundSearch(e.target.value)}
                  className="flex-1 min-w-[220px] rounded-full border border-gray-200 px-4 py-2 text-sm focus:outline-none focus:border-[#0F6E56]"
                  data-testid="admin-refund-search"
                />
                <label className="text-xs text-gray-500 flex items-center gap-2">
                  Desde
                  <input type="date" value={refundFrom} onChange={(e) => setRefundFrom(e.target.value)} className="rounded-full border border-gray-200 px-3 py-1.5 text-sm" data-testid="admin-refund-from" />
                </label>
                <label className="text-xs text-gray-500 flex items-center gap-2">
                  Hasta
                  <input type="date" value={refundTo} onChange={(e) => setRefundTo(e.target.value)} className="rounded-full border border-gray-200 px-3 py-1.5 text-sm" data-testid="admin-refund-to" />
                </label>
                {(refundSearch || refundFrom || refundTo) && (
                  <button
                    onClick={() => { setRefundSearch(""); setRefundFrom(""); setRefundTo(""); }}
                    className="text-xs text-[#0F6E56] font-semibold hover:underline"
                    data-testid="admin-refund-clear"
                  >
                    Limpiar filtros
                  </button>
                )}
                <span className="text-xs text-gray-500 ml-auto">
                  {filteredRefunds.length} de {refunds.length}
                </span>
              </div>
            )}
            {refunds.length === 0 ? (
              <div className="card text-center text-gray-500 py-10">
                No hay reembolsos registrados. ✅
              </div>
            ) : filteredRefunds.length === 0 ? (
              <div className="card text-center text-gray-500 py-10">
                Ningún reembolso coincide con los filtros aplicados.
              </div>
            ) : (
              filteredRefunds.map((r) => (
                <div key={r.id} className="rounded-2xl bg-white border border-red-100 p-5 flex items-center gap-4 flex-wrap" data-testid="admin-refund-row">
                  <div className="w-10 h-10 rounded-full bg-red-100 text-red-700 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{r.email}</div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {r.refunded_at ? formatDate(r.refunded_at) : "—"} · {r.product} · {r.amount ? `${(r.amount / 100).toFixed(2)} ${(r.currency || "eur").toUpperCase()}` : "—"}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5 truncate font-mono">
                      {r.charge_id}
                    </div>
                  </div>
                  <span className="badge bg-red-100 text-red-700">Reembolsado</span>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "personalizations" && (
          <div className="space-y-3" data-testid="admin-personalizations-list">
            <div className="rounded-2xl bg-[#0F6E56]/5 border border-[#0F6E56]/15 p-4 flex items-start gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg bg-[#0F6E56] text-[#E5C874] flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5" strokeWidth={2} />
              </div>
              <div className="flex-1">
                <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#0F6E56] mb-1">
                  Panel de recuperación
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Aquí aparecen los diagnósticos cuya interpretación LLM <strong>falló</strong> (aparece con texto genérico al usuario) o lleva más de <strong>5 minutos</strong> atascada en <em>pending</em>. Al pulsar <em>Regenerar</em>: se relanza la personalización con el pipeline robusto (retry + salvage por área), se regeneran los PDFs y <strong>se reenvía al cliente el email con los adjuntos actualizados</strong> en ~30-60 s.
                </p>
              </div>
              <button
                onClick={refreshFailedPersos}
                className="btn-ghost flex-shrink-0"
                data-testid="admin-perso-refresh-btn"
                title="Refrescar listado"
              >
                <RefreshCw className="w-4 h-4" />
                Refrescar
              </button>
            </div>

            {failedPersos.length === 0 ? (
              <div className="card text-center text-gray-500 py-10" data-testid="admin-perso-empty">
                No hay personalizaciones fallidas ni atascadas. ✅
              </div>
            ) : (
              failedPersos.map((p) => (
                <div
                  key={p.id}
                  className="rounded-2xl bg-white border border-amber-100 p-5 flex items-center gap-4 flex-wrap"
                  data-testid="admin-perso-row"
                >
                  <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate" title={p.email}>
                      {p.email || "—"}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {p.created_at ? formatDate(p.created_at) : "—"} · Score{" "}
                      <strong>{p.overall_score}/120</strong> · Nivel{" "}
                      <strong>{p.overall_level}</strong>
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1 font-mono truncate">
                      ID: {p.id}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className={`badge ${
                        p.personalization_status === "failed"
                          ? "bg-red-100 text-red-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                      title="Estado de la personalización LLM"
                    >
                      {p.personalization_status === "failed" ? "Fallida" : "Atascada"}
                    </span>
                    {p.team_readiness_status === "failed" && (
                      <span className="badge bg-orange-100 text-orange-700" title="Team Readiness Report también falló">
                        TR fallido
                      </span>
                    )}
                    <button
                      onClick={() => regeneratePersonalization(p)}
                      disabled={regeneratingId === p.id}
                      className="rounded-full px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60 flex items-center gap-2"
                      style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
                      data-testid={`admin-perso-regen-${p.id}`}
                      title="Regenerar la personalización y reenviar el email al cliente con los PDFs actualizados"
                    >
                      {regeneratingId === p.id ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Regenerando…
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          Regenerar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        {tab === "perceptions" && (
          <>
            <SectorsUsageCard data={sectorsUsage} />
            <div className="h-4" />
            <PerceptionsAdminPanel
              overview={perceptionOverview}
              tests={perceptionTests}
              loading={perceptionLoading}
              onRefresh={refreshPerception}
            />
          </>
        )}

        {tab === "consulting-failed" && (
          <FailedConsultingsPanel
            items={failedConsultings}
            regeneratingId={regeneratingConsultingId}
            resendingEmailId={resendingEmailId}
            onRegenerate={regenerateFailedConsulting}
            onResendEmail={resendEmailForConsulting}
            onRefresh={refreshFailedConsultings}
          />
        )}
      </main>
      {manualResult && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm"
          data-testid="admin-manual-result-modal"
          onClick={() => setManualResult(null)}
        >
          <div className="max-w-md w-full rounded-2xl bg-white shadow-2xl p-8" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4">
              <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "#0F6E56" }}>Activación manual completada</div>
              <h3 className="font-display text-xl font-bold text-gray-900">Contraseña temporal generada</h3>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                Comparte esta contraseña con <strong>{manualResult.email}</strong> por WhatsApp, llamada o email seguro. Pídele que la cambie al entrar por primera vez.
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 flex items-center justify-between gap-3 mb-4">
              <code className="font-mono text-lg font-semibold text-gray-900 break-all" data-testid="admin-manual-temp-password">{manualResult.password}</code>
              <button
                onClick={() => { navigator.clipboard.writeText(manualResult.password); toast.success("Copiado al portapapeles"); }}
                className="shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold text-white"
                style={{ background: "#0F6E56" }}
                data-testid="admin-manual-copy-btn"
              >
                Copiar
              </button>
            </div>
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
              ⚠ Este es el único momento en que verás la contraseña. Si la cierras sin copiar, deberás generar otra activando de nuevo.
            </p>
            <button
              onClick={() => setManualResult(null)}
              className="w-full rounded-full py-3 font-semibold text-sm text-white"
              style={{ background: "#0F6E56" }}
              data-testid="admin-manual-close-btn"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FailedConsultingsPanel({ items, regeneratingId, resendingEmailId, onRegenerate, onResendEmail, onRefresh }) {
  return (
    <div className="space-y-4" data-testid="admin-consulting-failed-panel">
      <div className="rounded-2xl bg-red-50/60 border border-red-200 p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-red-600 text-white flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-5 h-5" strokeWidth={2} />
        </div>
        <div className="flex-1">
          <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-red-700 mb-1">
            Consultorías fallidas · uso interno
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Tests en <code>generating_failed</code>, atascados en <code>generating</code> &gt;30 min, o con el <strong>email al cliente pendiente</strong> (Resend caído). Regenerar y reenviar email son idempotentes.
          </p>
        </div>
        <button
          onClick={onRefresh}
          className="btn-ghost flex-shrink-0"
          data-testid="admin-consulting-failed-refresh-btn"
          title="Refrescar listado"
        >
          <RefreshCw className="w-4 h-4" />Refrescar
        </button>
      </div>

      {items.length === 0 ? (
        <div className="card text-center text-gray-500 py-10" data-testid="admin-consulting-failed-empty">
          Ningún test de consultoría en fallo. ✨
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const isNotifFail = it.is_notification_failed;
            const badge = isNotifFail
              ? { text: "EMAIL PENDIENTE", cls: "bg-blue-100 text-blue-700" }
              : it.is_stuck
              ? { text: "ATASCADO", cls: "bg-amber-100 text-amber-700" }
              : { text: "FALLIDO", cls: "bg-red-100 text-red-700" };
            const borderCls = isNotifFail ? "border-blue-100" : "border-red-100";
            const displayError = isNotifFail ? it.notification_last_error : it.error;
            return (
              <div key={it.id} className={`rounded-2xl bg-white border ${borderCls} overflow-hidden`} data-testid="admin-consulting-failed-row">
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`badge ${badge.cls}`}>{badge.text}</span>
                        <span className="font-semibold text-slate-900 truncate max-w-[380px]" title={it.company_name}>
                          {it.company_name}
                        </span>
                        {it.sector && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                            {it.sector}
                          </span>
                        )}
                        {it.employees_count > 0 && (
                          <span className="text-[11px] text-slate-500">· {it.employees_count} empleados</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        Owner: <span className="text-slate-700">{it.user_email}</span> · Actualizado {formatDate(it.updated_at)}
                        {it.retry_count > 0 && (
                          <span className="text-amber-700"> · {it.retry_count} retry(s) de generación{it.retry_by_admin ? ` (último: ${it.retry_by_admin})` : ""}</span>
                        )}
                        {it.notification_attempts > 0 && (
                          <span className="text-blue-700"> · {it.notification_attempts} intento(s) de email</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isNotifFail ? (
                        <button
                          onClick={() => onResendEmail(it)}
                          disabled={resendingEmailId === it.id}
                          className="rounded-full px-4 py-2 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60 inline-flex items-center gap-2"
                          style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)" }}
                          data-testid={`admin-consulting-resend-btn-${it.id}`}
                          title="Reenviar email 'informe listo'"
                        >
                          {resendingEmailId === it.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <MessagesSquare className="w-4 h-4" />}
                          Reenviar email
                        </button>
                      ) : (
                        <button
                          onClick={() => onRegenerate(it)}
                          disabled={regeneratingId === it.id}
                          className="rounded-full px-4 py-2 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60 inline-flex items-center gap-2"
                          style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
                          data-testid={`admin-consulting-regenerate-btn-${it.id}`}
                          title="Regenerar informe"
                        >
                          {regeneratingId === it.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                          Regenerar
                        </button>
                      )}
                    </div>
                  </div>

                  {displayError && (
                    <div className={`mt-2 rounded-xl border p-3 text-xs font-mono leading-relaxed ${
                      isNotifFail ? "border-blue-200 bg-blue-50/60 text-blue-800" : "border-red-200 bg-red-50/60 text-red-800"
                    }`}>
                      {displayError}
                    </div>
                  )}

                  <div className="mt-3 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                    <div className="flex items-center gap-1">
                      <span className="text-slate-400 uppercase tracking-widest text-[10px] font-semibold">Bloques OK:</span>
                      {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                        <span
                          key={n}
                          className={`inline-flex items-center justify-center w-6 h-6 rounded font-bold text-[10px] ${
                            it.blocks_ok.includes(n) ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"
                          }`}
                          title={`Bloque ${n}: ${it.blocks_ok.includes(n) ? "OK" : "pendiente"}`}
                        >
                          {n}
                        </span>
                      ))}
                      <span className="ml-2 text-[10px] text-slate-400">
                        {it.blocks_ok.length}/7
                      </span>
                    </div>
                    {it.retry_at && !isNotifFail && (
                      <span>Último retry generación: {formatDate(it.retry_at)}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function SectorsUsageCard({ data }) {
  if (!data) {
    return (
      <div className="rounded-2xl bg-white border border-gray-100 p-5 text-sm text-slate-500" data-testid="admin-sectors-usage-loading">
        Cargando uso por sector…
      </div>
    );
  }
  const total = data.total_consulting_tests || 0;
  const rows = data.by_sector || [];
  const uncovered = data.uncovered_sectors || [];
  const covered = data.covered_sectors || [];
  return (
    <div className="rounded-2xl bg-white border border-gray-100 p-5" data-testid="admin-sectors-usage-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-[#0F6E56]">Uso por sector · Consultoría</div>
          <div className="text-xs text-slate-500 mt-1">
            {total} tests totales · {rows.filter(r => r.has_overlay).length} sectores con overlay activo · {covered.length} sectores cubiertos, {uncovered.length} sin overlay
          </div>
        </div>
        <div className="text-[10px] font-mono text-slate-400 text-right">iter84·G</div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-slate-400 italic py-2">Aún no hay tests de consultoría con sector rellenado.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-sectors-usage-table">
            <thead>
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                <th className="py-2 pr-3 font-semibold">Sector</th>
                <th className="py-2 pr-3 font-semibold text-right">Tests</th>
                <th className="py-2 pr-3 font-semibold text-center">Overlay activo</th>
                <th className="py-2 pr-3 font-semibold text-center">Origen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.sector} className="border-t border-slate-100" data-testid="admin-sectors-usage-row">
                  <td className="py-2 pr-3 truncate max-w-[360px]" title={r.sector}>{r.sector}</td>
                  <td className="py-2 pr-3 text-right font-semibold tabular-nums">{r.count}</td>
                  <td className="py-2 pr-3 text-center">
                    {r.has_overlay ? (
                      <span className="badge bg-emerald-100 text-emerald-700">Sí</span>
                    ) : (
                      <span className="badge bg-slate-100 text-slate-500">No</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-center">
                    {r.is_free_text ? (
                      <span className="text-[11px] text-slate-500 italic">Texto libre</span>
                    ) : covered.includes(r.sector) ? (
                      <span className="text-[11px] text-emerald-700">Cubierto</span>
                    ) : uncovered.includes(r.sector) ? (
                      <span className="text-[11px] text-amber-700">Sin overlay</span>
                    ) : (
                      <span className="text-[11px] text-slate-400">Fuera de catálogo</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-3 text-[11px] text-slate-400 leading-relaxed">
        Criterio "piloto antes de escalar": si un sector sin overlay concentra volumen creciente y hay evidencia sectorial verificable (Randstad, Adecco, Hays, INE, normativa oficial), es candidato al próximo overlay.
      </div>
    </div>
  );
}


function PerceptionsAdminPanel({ overview, tests, loading, onRefresh }) {
  if (!overview) {
    return (
      <div className="card text-center text-gray-500 py-10" data-testid="admin-perception-loading">
        {loading ? "Cargando…" : "Sin datos"}
      </div>
    );
  }
  const t = overview.tests || {};
  const r = overview.responses || {};
  const l = overview.llm || {};
  const s = overview.shares || {};
  const pctReached = t.with_token > 0 ? Math.round((r.tests_at_threshold / t.with_token) * 100) : 0;
  const openRate = s.by_purpose?.email > 0 ? Math.round((s.total_accesses / s.by_purpose.email) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="admin-perceptions-panel">
      <div className="rounded-2xl bg-[#0F6E56]/5 border border-[#0F6E56]/15 p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#0F6E56] text-[#E5C874] flex items-center justify-center flex-shrink-0">
          <MessagesSquare className="w-5 h-5" strokeWidth={2} />
        </div>
        <div className="flex-1">
          <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#0F6E56] mb-1">
            Contraste de Percepciones · uso interno
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Foto agregada del uso del feature: tests activos, respuestas del equipo, resúmenes/contrastes generados por Claude, enlaces PDF con token 15 días y actividad reciente. Sirve para detectar tests atascados o abuso de compartidos.
          </p>
        </div>
        <button
          onClick={onRefresh}
          className="btn-ghost flex-shrink-0"
          data-testid="admin-perceptions-refresh-btn"
          title="Refrescar métricas"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refrescar
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-perceptions-kpis">
        <Kpi icon={<Users className="w-4 h-4" />} label="Tests con link" value={t.with_token || 0} sub={`${t.total || 0} activados en total`} />
        <Kpi icon={<MessagesSquare className="w-4 h-4" />} label="Respuestas" value={r.total || 0} sub={`${r.tests_at_threshold || 0} tests en umbral (${pctReached}%)`} />
        <Kpi icon={<Sparkles className="w-4 h-4" />} label="Contrastes LLM" value={l.contrasts_total || 0} sub={`${l.summaries_total || 0} resúmenes · ${l.summaries_forced || 0} forzados`} />
        <Kpi icon={<LinkIcon className="w-4 h-4" />} label="Enlaces PDF" value={s.total || 0} sub={`${s.active || 0} activos · ${s.revoked || 0} revocados`} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={<ShieldCheck className="w-4 h-4" />} label="Aperturas totales" value={s.total_accesses || 0} sub={`~${openRate}% open-rate emails`} />
        <Kpi icon={<XCircle className="w-4 h-4" />} label="Revocados" value={s.revoked || 0} sub={`${s.expired || 0} expirados`} />
        <Kpi icon={<Zap className="w-4 h-4" />} label="Por email" value={s.by_purpose?.email || 0} sub={`${s.by_purpose?.self_download || 0} descarga propia`} />
        <Kpi icon={<AlertCircle className="w-4 h-4" />} label="Bloqueados <7" value={t.blocked_team_too_small || 0} sub={`${t.regenerated_tokens || 0} tokens regenerados`} />
      </div>

      <div className="rounded-2xl bg-white border border-gray-100 p-5" data-testid="admin-perceptions-timeline">
        <div className="flex items-center gap-2 mb-3">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#0F6E56]">Actividad reciente</div>
          <span className="text-xs text-slate-400">({(overview.recent_events || []).length})</span>
        </div>
        {(overview.recent_events || []).length === 0 ? (
          <div className="text-sm text-slate-400 italic">Aún no hay eventos.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {(overview.recent_events || []).map((e, i) => (
              <li key={i} className="py-2.5 flex items-start gap-3 text-sm" data-testid="admin-perceptions-event">
                <span className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${e.kind === "share_revoked" ? "bg-red-500" : "bg-emerald-500"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-slate-800">
                    {e.kind === "share_revoked" ? (
                      <>Revocado · <span className="text-slate-500">{e.sent_to || "—"}</span></>
                    ) : (
                      <>
                        {e.purpose === "email" ? "Enlace por email" : "Descarga propia"}
                        {e.sent_to && <> · <span className="text-slate-500">{e.sent_to}</span></>}
                      </>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 font-mono truncate">
                    {formatDate(e.at)}
                    {e.consulting_test_id && ` · test ${e.consulting_test_id.slice(-8)}`}
                    {e.kind === "share_created" && e.accessed_count > 0 && ` · ${e.accessed_count} aperturas`}
                    {e.revoked_by && ` · por ${e.revoked_by}`}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-2xl bg-white border border-gray-100 p-5" data-testid="admin-perceptions-tests-list">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#0F6E56]">Tests activos ({tests.length})</div>
          <div className="text-xs text-slate-400">Ordenados por más recientes</div>
        </div>
        {tests.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-4">Ningún test tiene el feature activado todavía.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                  <th className="py-2 pr-3 font-semibold">Empresa · Owner</th>
                  <th className="py-2 pr-3 font-semibold">Equipo</th>
                  <th className="py-2 pr-3 font-semibold">Respuestas</th>
                  <th className="py-2 pr-3 font-semibold">Regeneraciones</th>
                  <th className="py-2 pr-3 font-semibold">Último contraste</th>
                  <th className="py-2 pr-3 font-semibold">Enlaces activos</th>
                </tr>
              </thead>
              <tbody>
                {tests.map((row) => (
                  <tr key={row.consulting_test_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid="admin-perceptions-test-row">
                    <td className="py-2.5 pr-3 min-w-0">
                      <div className="font-medium text-slate-900 truncate max-w-[220px]" title={row.company_name || "—"}>{row.company_name || "—"}</div>
                      <div className="text-xs text-slate-500 truncate max-w-[220px]" title={row.user_email || "—"}>{row.user_email || "—"}</div>
                    </td>
                    <td className="py-2.5 pr-3">
                      <span className={`badge ${row.employees_count < 7 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{row.employees_count}</span>
                    </td>
                    <td className="py-2.5 pr-3">
                      <span className={row.threshold_reached ? "text-emerald-700 font-semibold" : "text-slate-600"}>
                        {row.responses_count}
                      </span>
                      <span className="text-slate-400 text-xs">/{row.threshold_required}</span>
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600">{row.regenerated_count || 0}</td>
                    <td className="py-2.5 pr-3">
                      {row.last_contrast_at ? (
                        <div className="max-w-[280px]">
                          <div className="text-xs text-slate-500">{formatDate(row.last_contrast_at)}</div>
                          <div className="text-slate-800 truncate" title={row.last_contrast_headline}>{row.last_contrast_headline || "—"}</div>
                        </div>
                      ) : <span className="text-slate-400 italic text-xs">Sin contraste aún</span>}
                    </td>
                    <td className="py-2.5 pr-3">
                      <span className={`badge ${row.shares_active > 0 ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>{row.shares_active}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-[11px] text-slate-400 text-right">
        Métricas generadas a las {formatDate(overview.generated_at)}
      </div>
    </div>
  );
}


function Kpi({ icon, label, value, sub }) {
  return (
    <div className="card !p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#0f6e56] font-semibold">
        {icon} {label}
      </div>
      <div className="font-display text-3xl font-bold text-gray-900 mt-2">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function TabBtn({ active, onClick, children, highlight, ...rest }) {
  return (
    <button
      onClick={onClick}
      className={`relative px-5 py-2 rounded-full text-sm font-medium transition-colors ${
        active
          ? "brand-gradient text-white shadow-md"
          : highlight
          ? "bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100"
          : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
      }`}
      {...rest}
    >
      {children}
      {highlight && !active && (
        <span
          aria-hidden
          className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-500 ring-2 ring-white animate-pulse"
        />
      )}
    </button>
  );
}
